<script setup lang="ts">
import type { Component } from "vue";
import { cn } from "~/lib/utils";

withDefaults(
  defineProps<{
    theme?: "primary" | "secondary" | "tertiary";
    mode?: "cards" | "items";
    position?: "left" | "right";
    verticalPosition?: "top" | "center";
    background?: boolean;
    title: string;
    icon?: Component;
    ctaLink?: string;
    ctaTitle?: string;
  }>(),
  {
    theme: "primary",
    mode: "items",
    position: "left",
    verticalPosition: "center",
    background: false,
    ctaTitle: "Scopri di più",
  }
);
</script>

<template>
  <div :class="cn(background && 'bg-primary-50', theme === 'secondary' && 'bg-primary', theme === 'tertiary' && 'bg-primary-950 text-white')">
    <div :class="cn('main-content py-12', (theme === 'tertiary' || theme === 'secondary') && 'py-14')">
      <template v-if="theme === 'primary'">
        <div :class="cn('grid grid-cols-1 lg:grid-cols-4', verticalPosition === 'top' && 'items-start', mode === 'items' && 'md:grid-cols-2 lg:gap-14', mode === 'cards' && 'gap-5')">
          <div :class="cn('flex flex-col justify-center mb-5 md:mb-0', mode === 'items' && 'mb-10', position === 'right' && 'lg:order-last col-span-2 lg:col-span-1')">
            <h2 class="text-4xl font-extrabold mb-5">
              <component :is="icon" class="w-12 h-12 text-themeGray mb-2"></component>
              {{ title }}
            </h2>
            <p v-if="'abstract' in $slots">
              <slot name="abstract"></slot>
            </p>
            <Button class="mt-8 max-w-max" v-if="ctaLink" as-child>
              <NuxtLink :to="ctaLink">{{ ctaTitle }}</NuxtLink>
            </Button>
          </div>
          <slot v-if="mode === 'cards'"></slot>
          <div class="col-span-3" v-else>
            <div class="flex flex-col gap-y-6">
              <slot></slot>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="theme === 'secondary'">
        <div class="grid grid-cols-1 lg:grid-cols-4 lg:gap-14">
          <div class="flex flex-col text-white mb-5 md:mb-0">
            <h2 class="text-4xl font-extrabold mb-5">
              <component :is="icon" class="w-12 h-12 mb-2"></component>
              {{ title }}
            </h2>
            <p class="mb-8 lg:mb-0" v-if="'abstract' in $slots">
              <slot name="abstract"></slot>
            </p>
          </div>
          <div class="col-span-3">
            <slot></slot>
          </div>
        </div>
      </template>

      <template v-else-if="theme === 'tertiary'">
        <h2 class="flex items-center text-4xl font-extrabold mb-10">
          <component :is="icon" class="w-12 h-12 me-2"></component>
          {{ title }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <slot></slot>
        </div>
        <div v-if="ctaLink" class="mt-16 text-end">
          <NuxtLink :href="ctaLink" :title="ctaTitle" class="font-semibold text-tertiary-400 hover:text-tertiary transition-colors">{{ ctaTitle }}</NuxtLink>
        </div>
      </template>
    </div>
  </div>
</template>
